import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import api, { mediaSrc } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useA11y } from '../context/AccessibilityContext';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { celebrate } from '../lib/celebrate';
import { CalendarClock, Inbox, MessagesSquare, Bot, Compass, Brain, Sparkles, Settings, LogOut, Menu, X, Waves, Sun, Moon, Crown, Zap, LifeBuoy, MessageSquareHeart, Layers, MailWarning, BatteryCharging, PartyPopper } from 'lucide-react';
import { LarkwellMark } from './LarkwellMark';
import GuidedTour from './GuidedTour';
import { FeedbackDialog } from './FeedbackDialog';

const NAV = [
  { to: '/app/today', label: 'Today', icon: CalendarClock, id: 'today' },
  { to: '/app/tasks', label: 'Tasks', icon: Inbox, id: 'tasks' },
  { to: '/app/projects', label: 'Projects', icon: Layers, id: 'projects' },
  { to: '/app/chats', label: 'Chats', icon: MessagesSquare, id: 'chats' },
  { to: '/app/agents', label: 'Agents', icon: Bot, id: 'agents' },
  { to: '/app/discover', label: 'Discover', icon: Compass, id: 'discover' },
  { to: '/app/memory', label: 'Memory', icon: Brain, id: 'memory' },
  { to: '/app/skills', label: 'Skills', icon: Sparkles, id: 'skills' },
  { to: '/app/settings', label: 'Settings', icon: Settings, id: 'settings' },
  { to: '/app/help', label: 'Help', icon: LifeBuoy, id: 'help' },
];

function Wordmark() {
  return (
    <div className="flex items-center gap-2">
      <div className="h-8 w-8 rounded-xl grid place-items-center text-white" style={{ background: 'hsl(var(--primary))' }}>
        <LarkwellMark className="h-5 w-5" stroke="#ffffff" />
      </div>
      <span className="font-display text-xl font-700 tracking-tight">Larkwell</span>
    </div>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { prefs, update } = useA11y();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [billing, setBilling] = useState(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [fbUnread, setFbUnread] = useState(0);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyDismissed, setVerifyDismissed] = useState(false);

  const resendVerification = async () => {
    setVerifyBusy(true);
    try {
      await api.post('/auth/verify/send');
      toast.success('Verification email sent \u2014 check your inbox.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send right now. Try again shortly.');
    } finally { setVerifyBusy(false); }
  };

  const showVerifyBanner = user && user.email_verified === false && !verifyDismissed;

  useEffect(() => {
    api.get('/billing/summary').then((r) => setBilling(r.data)).catch(() => {});
  }, [location.pathname]);

  // Conversion nudge: gently flag low/empty credits ONCE per session so free users
  // upgrade before hitting a hard wall mid-task. Pro users never see this.
  const lowCreditNudged = React.useRef(false);
  useEffect(() => {
    if (!billing || lowCreditNudged.current) return;
    if (billing.plan === 'pro') { lowCreditNudged.current = true; return; }
    const c = billing.credits ?? 0;
    if (c > 5) return;
    lowCreditNudged.current = true;
    const goUpgrade = () => navigate('/app/upgrade');
    if (c <= 0) {
      toast("You're out of credits for this month", {
        description: 'Upgrade to Pro for unlimited planning, or grab a quick top-up.',
        icon: <Zap className="h-4 w-4" style={{ color: 'hsl(var(--brand-honey))' }} />,
        duration: 12000,
        action: { label: 'Upgrade', onClick: goUpgrade },
      });
    } else {
      toast(`Only ${c} credit${c === 1 ? '' : 's'} left this month`, {
        description: 'Go unlimited with Larkwell Pro so planning never pauses.',
        icon: <Zap className="h-4 w-4" style={{ color: 'hsl(var(--brand-honey))' }} />,
        duration: 10000,
        action: { label: 'See Pro', onClick: goUpgrade },
      });
    }
  }, [billing, navigate]);

  useEffect(() => {
    if (!user?.is_admin) { setFbUnread(0); return; }
    api.get('/admin/feedback/unread_count').then((r) => setFbUnread(r.data?.unread || 0)).catch(() => {});
  }, [user?.is_admin, location.pathname]);

  // Verify Success: a little in-app celebration the moment someone confirms their email.
  const celebrateChecked = React.useRef(false);
  useEffect(() => {
    if (!user || celebrateChecked.current) return;
    celebrateChecked.current = true;
    let flag = null;
    try { flag = localStorage.getItem('larkwell_verified_celebrate'); } catch (e) {}
    if (flag) {
      try { localStorage.removeItem('larkwell_verified_celebrate'); } catch (e) {}
      setTimeout(() => celebrate(), 400);
      toast.success('Email verified — welcome to Larkwell!', {
        description: 'Your account is secure and ready. Let’s make today feel doable.',
        icon: <PartyPopper className="h-4 w-4" style={{ color: 'hsl(var(--primary))' }} />,
        duration: 9000,
      });
    }
  }, [user]);

  // Peak Learning: one gentle heads-up when Larkwell auto-adjusts your peak window.
  const peakNoticeChecked = React.useRef(false);
  useEffect(() => {
    if (!user || peakNoticeChecked.current) return;
    peakNoticeChecked.current = true;
    const fmt = (m) => { const h = Math.floor(m / 60), mm = m % 60; const ap = h >= 12 ? 'pm' : 'am'; const hh = h % 12 || 12; return mm ? `${hh}:${String(mm).padStart(2, '0')}${ap}` : `${hh}${ap}`; };
    api.get('/peak/notice').then((r) => {
      const shift = r.data?.shift;
      if (!shift?.to) return;
      toast(`Your peak focus looks closer to ${fmt(shift.to.start_min)}–${fmt(shift.to.end_min)} now.`, {
        description: 'Larkwell updated your plan to match. Edit anytime in Settings → Energy rhythm.',
        icon: <BatteryCharging className="h-4 w-4" style={{ color: 'hsl(var(--primary))' }} />,
        duration: 11000,
      });
      api.post('/peak/notice/seen').catch(() => {});
    }).catch(() => {});
  }, [user]);

  const initials = (user?.name || 'U').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();

  const SidebarInner = (
    <div className="h-full flex flex-col gap-1 p-3">
      <div className="flex items-center gap-2 px-2 py-2">
        <Wordmark />
        <button className="ml-auto md:hidden" onClick={() => setOpen(false)} aria-label="Close menu"><X className="h-5 w-5" /></button>
      </div>
      <nav className="flex flex-col gap-1 mt-2">
        {NAV.map((n) => (
          <NavLink key={n.id} to={n.to} onClick={() => setOpen(false)} data-testid={`sidebar-nav-${n.id}`}
            className={({ isActive }) => `flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm min-h-11 transition-colors ${isActive ? 'bg-card shadow-sm text-[hsl(var(--foreground))] font-600 border border-[hsl(var(--border))]' : 'text-[hsl(var(--foreground))]/75 hover:bg-card/70'}`}>
            <n.icon className="h-[18px] w-[18px]" /> <span className="flex-1">{n.label}</span>
            {n.id === 'settings' && fbUnread > 0 && (
              <span className="min-w-5 h-5 px-1.5 rounded-full grid place-items-center text-[11px] font-700 text-white" style={{ background: 'hsl(var(--primary))' }} data-testid="sidebar-feedback-badge">{fbUnread}</span>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="mt-auto space-y-2">
        {billing && (
          billing.plan === 'pro' ? (
            <div className="flex items-center gap-2 rounded-xl px-3 py-2.5 border border-[hsl(var(--primary)/0.3)]" style={{ background: 'hsl(var(--primary) / 0.08)' }}>
              <Crown className="h-4 w-4" style={{ color: 'hsl(var(--primary))' }} />
              <span className="text-xs font-600">Larkwell Pro</span>
            </div>
          ) : (
            <button onClick={() => { setOpen(false); navigate('/app/upgrade'); }} className="w-full text-left rounded-xl px-3 py-2.5 border bg-card hover:border-[hsl(var(--primary)/0.4)] transition-colors" style={{ borderColor: (billing.credits ?? 0) <= 5 ? 'hsl(var(--warning) / 0.6)' : 'hsl(var(--border))' }} data-testid="sidebar-upgrade-button">
              <div className="flex items-center gap-2"><Zap className="h-4 w-4" style={{ color: (billing.credits ?? 0) <= 5 ? 'hsl(var(--warning))' : 'hsl(var(--brand-honey))' }} /><span className="text-xs font-600 flex-1" style={{ color: (billing.credits ?? 0) <= 5 ? 'hsl(var(--warning))' : 'inherit' }}>{billing.credits} credit{billing.credits === 1 ? '' : 's'} left</span><Crown className="h-3.5 w-3.5" style={{ color: 'hsl(var(--primary))' }} /></div>
              <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">{(billing.credits ?? 0) <= 5 ? 'Go unlimited with Pro →' : 'Upgrade to Pro →'}</p>
            </button>
          )
        )}
        <div className="flex items-center gap-2 rounded-xl border border-[hsl(var(--border))] bg-card px-3 py-2.5">
          <Waves className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
          <span className="text-xs font-500 flex-1">Low-stim mode</span>
          <Switch checked={prefs.low_stim} onCheckedChange={(v) => update({ low_stim: v })} data-testid="sidebar-lowstim-toggle" />
        </div>
        <button onClick={() => { setOpen(false); setFeedbackOpen(true); }} className="w-full flex items-center gap-2 rounded-xl border border-[hsl(var(--border))] bg-card px-3 py-2.5 hover:bg-[hsl(var(--secondary))] transition-colors" data-testid="sidebar-feedback-button">
          <MessageSquareHeart className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />
          <span className="text-xs font-500 flex-1 text-left">Send feedback</span>
        </button>
        <button onClick={() => update({ theme: prefs.theme === 'dark' ? 'light' : 'dark' })} className="w-full flex items-center gap-2 rounded-xl border border-[hsl(var(--border))] bg-card px-3 py-2.5 hover:bg-[hsl(var(--secondary))] transition-colors" data-testid="sidebar-theme-toggle">
          {prefs.theme === 'dark' ? <Sun className="h-4 w-4 text-[hsl(var(--muted-foreground))]" /> : <Moon className="h-4 w-4 text-[hsl(var(--muted-foreground))]" />}
          <span className="text-xs font-500 flex-1 text-left">{prefs.theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="w-full flex items-center gap-2.5 rounded-xl px-2.5 py-2.5 hover:bg-card/70 transition-colors" data-testid="user-menu-button">
              <Avatar className="h-8 w-8">
                {(user?.avatar_media_id || user?.picture) ? <AvatarImage src={user?.avatar_media_id ? mediaSrc(user.avatar_media_id) : user.picture} /> : null}
                <AvatarFallback className="text-xs" style={{ background: 'hsl(var(--secondary))' }}>{initials}</AvatarFallback>
              </Avatar>
              <div className="text-left min-w-0">
                <p className="text-sm font-600 truncate max-w-[150px]">{user?.name}</p>
                <p className="text-[11px] text-[hsl(var(--muted-foreground))] truncate max-w-[150px]">{user?.email}</p>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuItem onClick={() => navigate('/app/settings')}><Settings className="h-4 w-4 mr-2" /> Settings</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={async () => { await logout(); navigate('/'); }} data-testid="logout-button"><LogOut className="h-4 w-4 mr-2" /> Sign out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  return (
    <div className="h-dvh grid grid-cols-1 md:grid-cols-[280px_1fr] bg-[hsl(var(--background))]">
      <aside className="hidden md:block border-r border-[hsl(var(--border))] bg-[hsl(var(--surface-2))]">{SidebarInner}</aside>

      <div className="md:hidden flex items-center gap-2 px-4 h-14 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface-2))]">
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-button" aria-label="Open menu"><Menu className="h-5 w-5" /></button>
        <Wordmark />
      </div>
      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <div className="relative w-72 bg-[hsl(var(--surface-2))] border-r border-[hsl(var(--border))]">{SidebarInner}</div>
        </div>
      )}

      <main className="overflow-hidden min-h-0 flex flex-col">
        {showVerifyBanner && (
          <div className="flex items-center gap-3 px-4 sm:px-6 py-2.5 border-b border-[hsl(var(--border))]" style={{ background: 'hsl(var(--warning) / 0.12)' }} data-testid="verify-email-banner">
            <MailWarning className="h-4 w-4 shrink-0" style={{ color: 'hsl(var(--warning))' }} />
            <p className="text-[13px] leading-snug flex-1 min-w-0">
              <span className="font-600">Verify your email</span>
              <span className="text-[hsl(var(--muted-foreground))]"> — confirm {user?.email} to keep your account secure.</span>
            </p>
            <Button size="sm" variant="secondary" className="h-8 rounded-lg border border-[hsl(var(--border))] shrink-0" onClick={resendVerification} disabled={verifyBusy} data-testid="verify-email-resend">
              {verifyBusy ? 'Sending…' : 'Resend link'}
            </Button>
            <button onClick={() => setVerifyDismissed(true)} className="shrink-0 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors" aria-label="Dismiss" data-testid="verify-email-dismiss"><X className="h-4 w-4" /></button>
          </div>
        )}
        <div className="flex-1 overflow-hidden min-h-0">
          <Outlet />
        </div>
      </main>

      <GuidedTour />
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </div>
  );
}
